import { DefaultButton, IconButton, IContextualMenuItem, Label, Link, MessageBar, Persona, Spinner, SpinnerSize, TooltipHost } from '@fluentui/react';
import { PatientPreferredDoctor } from '../../api/patients.types';
import { Patient } from '../../api';
import { SecondaryContact } from '../../components/patient/SecondaryContact';
import { SYMPTOMS_HIGHLIGHTS } from '../../config/constants';
import moment from 'moment-timezone';
import * as React from 'react';
import 'react-block-ui/style.css';
import Highlighter from 'react-highlight-words';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { OnSiteVisit } from '../../redux/on-site-manager/on-site-visits/types';
import { PatientFileData } from '../../redux/patient-files/types';
import { EditPatientRequest } from '../../redux/patient/types';
import { stemmer } from '../../util/Vendor';
import { Market } from '../../api/markets.types';
import { IMAGES } from '../../config/images';
import { ROUTES } from '../../config/routes';
import { HealAPI } from '../../healApi';
import { fetchOneLatestClaimByVisitCode, generateClaim } from '../../redux/claim/actions';
import { Claim } from '../../redux/claim/types';
import { AsyncData } from '../../redux/core';
import { toast } from '../../redux/message/actions';
import { ToastParams } from '../../redux/message/types';
import { fetchOnSiteVisit, setOnSiteVisit } from '../../redux/on-site-manager/on-site-visits/actions';
import { RootState } from '../../redux/types';
import { setVisitCode } from '../../redux/ui/actions';
import { endVisit, fetchVisit, setVisit, startVisit, VisitsAction } from '../../redux/visits/actions';
import { ConfirmationStateType, Payer, Visit } from '../../redux/visits/types';
import { BillStatus, StatusOptions, VisitStatus } from '../../types';
import { SubService } from '../../api/service-offerings-types';
import { css } from '../../util/Css';
import { friendlyAge } from '../../util/Date';
import { File } from '../../util/File';
import { Clipboard } from '../core/Clipboard';
import { HealHqMap } from '../core/HealHqMap';
import { InfoMessage } from '../core/InfoMessage';
import { InlineEditField } from '../core/InlineEditField';
import { PreferredDoctorPersona } from '../core/PreferredDoctorPersona';
import { PatientFiles } from '../patient/PatientFiles';
import { PatientFlags } from '../patient/PatientFlags';
import { PatientNotes } from '../patient/PatientNotes';
import { InsuranceTransactionLinks } from '../tool/insurance/InsuranceTransactionLinks';
import { CollapsibleSection } from './CollapsibleSection';
import { AddInsuranceDialog } from './dialogs/AddInsuranceDialog';
import CancelVisitDialog from './dialogs/CancelVisitDialog';
import { ChangeProviderDialog } from './dialogs/ChangeProviderDialog';
import { CheckOutVisitDialog } from './dialogs/CheckOutVisitDialog';
import { EditAddressDialog } from './dialogs/EditAddressDialog';
import { EndVisitDialog } from './dialogs/EndVisitDialog';
import { StartVisitDialog } from './dialogs/StartVisitDialog';
import { UpdateTimeDialog } from './dialogs/UpdateTimeDialog';
import PatientConfirmation from './PatientConfirmation';
import { VisitLog } from './VisitLog';
import './VisitSidePanel.scss';
import { getIconDetails } from './VisitStatusIcon';
import { FeatureFlagContext } from '../feature-flag/FeatureFlagProvider';
import { ChangeServiceOfferingDialog } from './dialogs/ChangeServiceOfferingDialog';
import { withRouter } from '../../util/Router';
import { NavigateFunction, Location } from 'react-router-dom';
import { VisitWaitlistTitle, VisitWaitlistToggle } from './VisitWaitlistToggle';

declare const healConfig: any;

export interface OwnProps {
  visit: Visit;
  hideMap?: boolean;
  hideCloseIcon?: boolean;
  onClose?: () => void;
  showReferralPanel?: boolean;
  zendeskTicketId?: string;
  location?: Location;
  navigate?: NavigateFunction;
}

interface StateProps {
  markets: AsyncData<Market[]>;
  payers?: Payer[];
  claim?: Claim;
  carePlan?: PatientFileData;
  subService?: SubService;
}

interface DispatchProps {
  actions: {
    setVisitCode: (code: string) => void;
    setVisit: (visit: Visit) => void;
    setOnSiteVisit: (value: OnSiteVisit) => void;
    refreshVisits: () => void;
    startVisit: (visitCode: string, dateTime: Date) => void;
    endVisit: (visitCode: string, dateTime: Date) => void;
    fetchVisit: (visitCode: string, refresh?: boolean) => void;
    fetchOnSiteVisit: (visitCode: string, refresh?: boolean) => void;
    toast: (params: ToastParams) => void;
    generateClaim: (visitCode: string, ignoreOldDataCheck: boolean, forceFinalizedClaimReplacement: boolean) => void;
    fetchOneLatestClaimByVisitCode: (visitCode: string) => void;
  };
}

export type VisitSidePanelProps = OwnProps & StateProps & DispatchProps;

export interface VisitSidePanelState {
  currentLogText: string;
  preferredDoctor: AsyncData<PatientPreferredDoctor>;
  carePlanDownloading: boolean;
  patientData?: EditPatientRequest;
  phoneNumberLineType: AsyncData<string>;
  showReferralPanel: boolean;
  codeOfVisitBeingUpdated?: string;
  showCancelVisitDialog: boolean;
  patient: AsyncData<Patient>;
}

class Component extends React.Component<VisitSidePanelProps, VisitSidePanelState> {
  context!: React.ContextType<typeof FeatureFlagContext>;

  constructor(props: VisitSidePanelProps) {
    super(props);
    this.state = {
      currentLogText: '',
      preferredDoctor: { isFetching: false },
      carePlanDownloading: false,
      phoneNumberLineType: { isFetching: false },
      showReferralPanel: this.props.showReferralPanel ?? false,
      showCancelVisitDialog: false,
      patient: { isFetching: false },
    };
  }

  public componentDidUpdate(prevProps: VisitSidePanelProps) {
    if (this.props.visit.visitCode !== prevProps.visit.visitCode) {
      this.fetchPatientData(this.props.visit);
      this.getPatientDetails();
      this.fetchPreferredDoctor(this.props.visit.patientId);

      // send action to fetch next potential claim
      this.props.actions.fetchOneLatestClaimByVisitCode(this.props.visit.visitCode);

      this.setState({ phoneNumberLineType: { data: undefined, isFetching: false } });
    }
  }

  public componentDidMount() {
    this.fetchPatientData(this.props.visit);
    this.fetchPreferredDoctor(this.props.visit.patientId);
    this.getPatientDetails();

    this.props.actions.fetchOneLatestClaimByVisitCode(this.props.visit.visitCode);
  }

  public render(): JSX.Element {
    const { visit } = this.props;
    const { preferredDoctor } = this.state;

    const showPatientConfirmation = visit.confirmationState !== ConfirmationStateType.EXEMPT && visit.visitServiceCode !== 'HUB_INSTALL';

    return (
      <div>
        {this.renderHeader()}
        {this.state.showCancelVisitDialog && (
          <CancelVisitDialog
            visit={this.props.visit}
            onSuccess={(visit) => this.onVisitUpdated(visit)}
            onCancel={() => this.setState({ showCancelVisitDialog: false })}
            actions={{ toast: this.props.actions.toast }}
          />
        )}
        {!this.props.hideMap && this.renderMap()}
        <CollapsibleSection id="visit_details" title="Details">
          {showPatientConfirmation && <PatientConfirmation visit={visit} onUpdate={() => this.refreshVisit()} />}
          {this.renderFieldValue(
            <VisitWaitlistTitle />,
            <VisitWaitlistToggle
              visit={visit}
              onError={(content) => this.props.actions.toast({ type: 'error', title: 'Error', content })}
              onSuccess={(title, content, visitCode?) => {
                this.props.actions.toast({ type: 'success', title, content });
                this.refreshVisit(visitCode);
              }}
            />
          )}
          {this.renderFieldValue('Files', <PatientFiles visits={{ isFetching: false }} patientId={visit.patientId} visitCode={visit.visitCode} />)}
          {this.renderFieldValue('Date Requested', this.getFormattedTime(visit.timeRequested))}
          {this.renderFieldValue('Requested Time Slot', this.getFormattedRequestedTimeSlot())}
          {this.renderFieldValue('System Time Scheduled For', this.getFormattedTime(visit.scheduledTime))}
          {this.props.subService && this.renderFieldValue('Sub Service', this.props.subService.text)}
          {this.renderEditableField(
            'Symptoms',
            'updateSymptoms',
            ['symptoms'],
            [visit.symptoms],
            <Highlighter
              highlightClassName="symptoms-highlight"
              searchWords={SYMPTOMS_HIGHLIGHTS}
              autoEscape={true}
              textToHighlight={visit.symptoms || ''}
              findChunks={this.findChunks}
            />,
            visit.symptoms,
            true
          )}
          {this.renderEditableField('Visit Notes', 'editInfo', ['notes'], [visit.notes], undefined, visit.notes, true)}
          {visit.disposition && this.renderFieldValue('Disposition', visit.disposition)}
          <Clipboard className="cursor-copy" content={visit.addressLong}>
            {this.renderFieldValue('Location', visit.addressLong)}
          </Clipboard>
          {this.renderFieldValue('Suite/Unit', visit.unit)}
          {this.renderFieldValue('Entry Instructions', visit.instructions)}
        </CollapsibleSection>
        <CollapsibleSection id="visit_patient_notes" title="Patient Notes">
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <PatientNotes patientId={visit.patientId} visitCode={visit.visitCode} />
          </div>
        </CollapsibleSection>
        <CollapsibleSection id="visit_patient_details" title="Patient">
          {this.renderFieldValue('Relationship', 'Account Owner', visit.relationshipName === 'You')}
          {this.renderFieldValue(
            'Relationship',
            <span>
              {visit.relationshipName} of Account Owner -- {visit.buildUserAccountFullName}
            </span>,
            visit.relationshipName !== 'You'
          )}
          {this.renderEditableField(
            'Patient Name',
            'updatePatient',
            ['firstName', 'lastName'],
            [visit.patientFirstName, visit.patientLastName],
            undefined,
            `${visit.patientFirstName} ${visit.patientLastName}`
          )}
          {this.renderFieldValue('Flags', <PatientFlags patientId={visit.patientId} visitCode={visit.visitCode} />)}
          {this.renderFieldValue('Gender', visit.genderName)}
          {this.renderEditableField('Date Of Birth', 'updatePatient', ['dateOfBirth'], [visit.patientDob], undefined, visit.patientDob)}
          {this.renderFieldValue('Age', friendlyAge(visit.patientDob), true, 'HORIZONTAL')}
          {this.renderPhoneNumber()}
          {this.renderSecondaryContact()}
          {this.renderFieldValue('Current Preferred Provider', <PreferredDoctorPersona preferredDoctor={preferredDoctor} linkToProfile={true} />)}
          {visit.relationshipName !== 'You' && this.renderEditableField('Email', 'updatePatient', ['email'], [visit.patientEmail], undefined, visit.patientEmail)}
          {visit.relationshipName === 'You' && (
            <Clipboard className="cursor-copy" content={visit.patientEmail}>
              {this.renderFieldValue('Email', visit.patientEmail)}
            </Clipboard>
          )}
          {this.renderFieldValue('Proxy Number', visit.proxyNumber || 'N/A')}
          {this.renderFieldValue('Completed Visits', visit.patientCompletedVisits)}
          {this.renderFieldValue('Cancelled Visits', visit.patientCancelledVisits)}
          {this.renderCarePlanLink()}
        </CollapsibleSection>
        <CollapsibleSection id="visit_provider_details" title="Provider">
          {this.renderFieldValue('Provider', <Link href={`${ROUTES.providers}/${visit.doctorId}`}>{visit.buildDoctorFullName}</Link>, !!visit.doctorId)}
          <Clipboard className="cursor-copy" content={visit.doctorPhone}>
            {this.renderFieldValue('Provider Phone Number', visit.doctorPhone, !!visit.doctorId)}
          </Clipboard>
          {this.renderFieldValue('Provider', 'Not assigned', !visit.doctorId)}
          {this.renderFieldValue('Assistant', <Link href={`${ROUTES.providers}/${visit.assistantId}`}>{visit.buildAssistantFullName}</Link>, !!visit.assistantId)}
          <Clipboard className="cursor-copy" content={visit.assistantPhone}>
            {this.renderFieldValue('Assistant Phone Number', visit.assistantPhone, !!visit.assistantId)}
          </Clipboard>
          {this.renderFieldValue('Assistant', 'Not assigned', !visit.assistantId)}
        </CollapsibleSection>
        <CollapsibleSection id="visit_insurance" title="Primary Insurance">
          {this.renderFieldValue('Insurance Provider', visit.insuranceName || 'No Insurance')}
          {this.renderFieldValue('Member Id', visit.insuranceMemberId || 'N/A')}
          {this.renderFieldValue('Group Id', visit.insuranceGroupId || 'N/A')}
          {this.renderFieldValue('Insurance Response', visit.insuranceResponseStatus || 'N/A')}
        </CollapsibleSection>
        <CollapsibleSection id="visit_billing" className="billing" title="Billing">
          {this.renderPendingChargeMessage()}
          {this.renderFieldValue('Status', visit.billStatus, true, 'HORIZONTAL')}
          {this.renderFieldValue('Original Amount', `$${(visit.originalAmount || 0).toFixed(2)}`, true, 'HORIZONTAL')}
          {this.renderFieldValue(
            'Extra Charge',
            `$${(visit.chargeAmount + visit.discountAmount + visit.refundedAmount - visit.originalAmount || 0).toFixed(2)}`,
            true,
            'HORIZONTAL'
          )}
          {this.renderFieldValue('Promo Code', visit.billPromoCode || 'None', true, 'HORIZONTAL')}
          {this.renderFieldValue('Amount Discounted', `($${(visit.discountAmount || 0).toFixed(2)})`, true, 'HORIZONTAL')}
          {this.renderFieldValue('Refunded', `($${(visit.refundedAmount || 0).toFixed(2)})`, true, 'HORIZONTAL')}
          {this.renderFieldValue('Visit Total', `$${(visit.chargeAmount || 0).toFixed(2)}`, true, 'HORIZONTAL')}
          {this.renderFieldValue('Payment Method', this.getPaymentMethod(), true, 'HORIZONTAL')}
          {visit.insurancePlanAppliedBenefitType === 'VISIT_CREDIT' && this.renderFieldValue('Visit Credit Benefit Applied', visit.insurancePlanAppliedBenefitCode)}
          {visit.appliedSubscriptionContractName != null && this.renderFieldValue('Subscription Used', visit.appliedSubscriptionContractName)}
        </CollapsibleSection>
        <CollapsibleSection id="visit_claim" title="Claim">
          {this.renderClaimSection()}
        </CollapsibleSection>
        <CollapsibleSection id="visit_log" title="Log">
          <VisitLog visitCode={visit.visitCode} updatedAt={visit.updatedAt} />
        </CollapsibleSection>
        {healConfig && healConfig.env !== 'prd' && (
          <CollapsibleSection id="visit_debug" title="Debug">
            <pre>{JSON.stringify(this.props.visit, null, 2)}</pre>
          </CollapsibleSection>
        )}
      </div>
    );
  }

  private findChunks = ({ searchWords, textToHighlight }: { searchWords: any; textToHighlight: string }) => {
    const words = (textToHighlight || '').split(' ');

    const potentialChunks = words.reduce((acc: any, word) => {
      const start = acc.length === 0 ? 0 : acc[acc.length - 1].end + 1;
      return [
        ...acc,
        {
          start,
          end: start + word.length,
          word,
        },
      ];
    }, []);

    const stemmedSearchWords = new Set(searchWords.map(stemmer));

    const chunks = potentialChunks.map(({ word, ...chunk }: any) => (stemmedSearchWords.has(stemmer(word)) ? chunk : null));

    return chunks.filter(Boolean);
  };

  private getPhoneNumberLineType(phoneNumber: string) {
    let lineType: string | undefined = this.state.phoneNumberLineType.data;
    this.setState({ phoneNumberLineType: { data: lineType, isFetching: true } });
    HealAPI.getPhoneNumberLineType(phoneNumber)
      .then((response) => {
        if (phoneNumber !== this.props.visit.patientPhone) {
          return;
        }
        if (response.ok && response.data && response.data.data && response.data.data !== 'UNKNOWN') {
          lineType = response.data.data;
        } else {
          this.props.actions.toast({
            type: 'error',
            title: 'Error',
            content: 'An error occurred, please try again later',
          });
        }

        this.setState({ phoneNumberLineType: { data: lineType, isFetching: false } });
      })
      .catch((e) => {
        this.props.actions.toast({
          type: 'error',
          title: 'Error',
          content: e.error || 'An error occurred, please try again later',
        });

        this.setState({ phoneNumberLineType: { data: lineType, isFetching: false } });
      });
  }

  private renderCarePlanLink(): JSX.Element | null {
    if ((this.props.visit.status === VisitStatus.FULLY_PAID || this.props.visit.status === VisitStatus.REFUNDED) && this.props.carePlan) {
      return (
        <div className="flex" style={{ marginBottom: 15 }}>
          <Link disabled={this.state.carePlanDownloading} onClick={this.onDownloadCarePlanClick}>
            Download Heal Care Plan
          </Link>
          {this.state.carePlanDownloading && <Spinner style={{ marginLeft: 5 }} />}
        </div>
      );
    }
    return null;
  }

  private onDownloadCarePlanClick = () => {
    this.downloadCarePlan();
  };

  private renderPendingChargeMessage(): JSX.Element | null {
    const { visit } = this.props;
    if (!visit.delayedPayment || visit.billStatus !== BillStatus.PENDING_CHARGE) {
      return null;
    }
    return (
      <InfoMessage>
        <div>Patient hasn&#39;t yet been charged. Charged amount might change before the payment is processed.</div>
      </InfoMessage>
    );
  }

  private renderEditableField(
    label: string,
    apiEndpoint: string,
    fieldNames: string[],
    values: string[],
    valueNotEditing?: JSX.Element,
    clipboardValue?: string | number,
    multiline?: boolean
  ): JSX.Element | null {
    let labelContent: string | JSX.Element = label;
    if (clipboardValue) {
      labelContent = (
        <Clipboard className="cursor-copy" content={'' + clipboardValue}>
          {label}
        </Clipboard>
      );
    }

    return this.renderFieldValue(
      labelContent,
      <InlineEditField
        fieldNames={fieldNames}
        initialValues={values}
        valueNotEditing={valueNotEditing}
        visitCode={this.props.visit.visitCode}
        multiline={multiline}
        onSubmit={(newValue) => this.onSubmit(apiEndpoint, newValue)}
      />
    );
  }

  private renderFieldValue(label: string | JSX.Element, value: string | number | JSX.Element, condition = true, orientation: 'HORIZONTAL' | 'VERTICAL' = 'VERTICAL'): JSX.Element {
    if (!condition) {
      return <div />;
    }
    return (
      <div className={css('visit-field-control', { horizontal: orientation === 'HORIZONTAL' })}>
        <div className="visit-field-label">{label}</div>
        <div className="visit-field-value">{value}</div>
      </div>
    );
  }

  private renderPhoneNumber(): JSX.Element {
    const { visit } = this.props;
    return (
      <div className={css('visit-field-control')}>
        <div className="visit-field-label">
          <Clipboard className="cursor-copy" content={visit.patientPhone}>
            Phone Number
          </Clipboard>
        </div>
        <div className="flex flex-row">
          {this.renderLineType()}
          <div className="visit-field-value flex">
            <InlineEditField
              fieldNames={['phoneNumber']}
              initialValues={[visit.patientPhone]}
              valueNotEditing={undefined}
              visitCode={visit.visitCode}
              multiline={undefined}
              onSubmit={(newValue) => this.editPhoneNumber(newValue)}
            />
          </div>
          {!this.state.phoneNumberLineType.data && (
            <IconButton
              iconProps={{
                iconName: 'Message',
              }}
              ariaLabel="Check SMS"
              onClick={() => this.getPhoneNumberLineType(visit.patientPhone)}
              disabled={this.state.phoneNumberLineType.isFetching}
              text="Resolve phone type"
              style={{ marginTop: 6 }}
            />
          )}
        </div>
      </div>
    );
  }

  private getPatientDetails(): void {
    this.setState({
      patient: {
        isFetching: true,
      },
    });
    const {
      visit: { patientId },
    } = this.props;
    HealAPI.fetchPatient(patientId).then((response) => {
      const patient = response.data.data;
      if (response.ok && patient) {
        this.setState({
          patient: {
            isFetching: false,
            data: patient,
            error: '',
          },
        });
      } else {
        this.setState({
          patient: {
            isFetching: false,
            data: undefined,
            error: "An error occured when retrieving the patient's secondary contact information",
          },
        });
      }
    });
  }

  private renderSecondaryContact(): JSX.Element | undefined {
    const patient = this.state.patient.data;

    return (
      <div className={css('visit-field-control')}>
        <div className="visit-field-label">
          <Clipboard className="cursor-copy" content={patient?.secondaryContact?.phone ?? ''}>
            Secondary Contact
          </Clipboard>
        </div>
        <div className="flex flex-row">
          {!patient ? (
            <p>Loading...</p>
          ) : (
            <SecondaryContact
              patient={patient}
              hideLabel
              onUpdated={(patient) =>
                this.setState({
                  patient: {
                    isFetching: false,
                    data: patient,
                    error: '',
                  },
                })
              }
            />
          )}
        </div>
      </div>
    );
  }

  private renderLineType(): JSX.Element | undefined {
    if (!this.state.phoneNumberLineType.data) {
      return;
    }
    if (this.state.phoneNumberLineType.isFetching) {
      return (
        <div className="flex">
          <Spinner size={SpinnerSize.small} />
        </div>
      );
    }
    const lineType = this.state.phoneNumberLineType.data;
    const bgColor: string = lineType === 'MOBILE' ? 'green' : 'grey';
    const textable: boolean = lineType === 'MOBILE';
    let lineTypeToDisplay: string = lineType;
    switch (lineType) {
      case 'MOBILE':
        lineTypeToDisplay = 'Mobile';
        break;
      case 'LANDLINE':
        lineTypeToDisplay = 'Landline';
        break;
      case 'VOIP':
        lineTypeToDisplay = 'VoIP';
        break;
    }
    return (
      <div className="flex" style={{ paddingTop: 5, paddingBottom: 10 }}>
        <TooltipHost content={textable ? 'Supports texting' : "Doesn't support texting"}>
          <Label
            style={{
              color: 'white',
              backgroundColor: bgColor,
              borderRadius: 3,
              fontWeight: 600,
              paddingTop: 0,
              paddingRight: 5,
              paddingBottom: 0,
              paddingLeft: 5,
              transition: 'opacity .2s linear',
            }}
          >
            {lineTypeToDisplay}
          </Label>
        </TooltipHost>
      </div>
    );
  }

  private editPhoneNumber(newValue: any): any {
    if (newValue['phoneNumber'] !== this.props.visit.patientPhone) {
      this.setState({ phoneNumberLineType: { data: undefined, isFetching: false } });
    }
    return this.onSubmit('updatePatient', newValue);
  }

  private renderHeader(): JSX.Element {
    const { visit, hideCloseIcon } = this.props;
    const icon = getIconDetails(visit.status);

    return (
      <div className="header">
        <div className="header-action-container">
          <Clipboard className="header-visit-code cursor-copy" content={visit.visitCode}>
            <div>{visit.visitCode}</div>
          </Clipboard>
          {this.renderActions()}
          {!hideCloseIcon && (
            <IconButton
              iconProps={{ iconName: 'Cancel' }}
              title="Close"
              ariaLabel="Close"
              onClick={() => (this.props.onClose ? this.props.onClose() : this.props.actions.setVisitCode(''))}
            />
          )}
        </div>
        <Persona
          className="persona"
          text={visit.patientName}
          secondaryText={visit.patientEmail}
          onRenderPrimaryText={() => <Link href={`${ROUTES.patients}/${visit.patientId}`}>{visit.buildPatientFullName}</Link>}
        />
        <div className="visit-status-container">
          <div className="service-code">{visit.visitServiceName}</div>
          <div className="market-code">{visit.marketCode}</div>
          <div className="visit-status" style={{ color: icon.color }}>
            <i className={`ms-font-xxl ms-Icon ms-Icon--${icon.icon}`} style={{ color: icon.color }} />
            <span style={{ padding: 8 }}>{visit.status}</span>
          </div>
        </div>
      </div>
    );
  }

  private renderActions(): JSX.Element | null {
    const { visit } = this.props;

    const items: IContextualMenuItem[] = [];
    if ((visit.availableActions.startVisit && !visit.hasCampaign) || (visit.availableActions.startVisit && visit.hasCampaign && visit.visitServiceCode !== 'FLUSHOT_ADULT')) {
      items.push({
        key: 'startVisit',
        name: 'Start Visit',
        iconProps: { iconName: 'Running' },
        onClick: () =>
          StartVisitDialog.show({
            visit,
            onClosed: this.onDialogClosed,
            actions: { toast: this.props.actions.toast },
          }),
      });
    }

    if ((visit.availableActions.endVisit && !visit.hasCampaign) || (visit.availableActions.endVisit && visit.hasCampaign && visit.visitServiceCode !== 'FLUSHOT_ADULT')) {
      items.push({
        key: 'endVisit',
        name: 'End Visit',
        iconProps: { iconName: 'Accept' },
        onClick: () =>
          EndVisitDialog.show({
            visit,
            onClosed: this.onDialogClosed,
            actions: { toast: this.props.actions.toast },
          }),
      });
    }

    if (visit.availableActions.startVisit && visit.hasCampaign && visit.visitServiceCode === 'FLUSHOT_ADULT') {
      items.push({
        key: 'checkOutVisit',
        name: 'Check out Visit',
        iconProps: { iconName: 'Accept' },
        onClick: () =>
          CheckOutVisitDialog.show({
            visit,
            onClosed: this.onDialogClosed,
            actions: { toast: this.props.actions.toast },
          }),
      });
    }

    if (visit.availableActions.startVisit && !visit.hasCampaign) {
      items.push({
        key: 'editAddress',
        name: 'Edit Address',
        iconProps: { iconName: 'MapPin' },
        onClick: () =>
          EditAddressDialog.show({
            visit,
            onClosed: this.onDialogClosed,
            actions: { toast: this.props.actions.toast },
          }),
      });
    }

    if (visit.availableActions.changeDoctor && !visit.hasCampaign) {
      items.push({
        key: 'changeDoctor',
        name: 'Update Provider and Time',
        iconProps: { iconName: 'Medical' },
        onClick: () =>
          ChangeProviderDialog.show({
            visit,
            preferredDoctor: this.state.preferredDoctor.data,
            onSuccess: (visit) => this.onVisitUpdated(visit),
          }),
      });
    }

    if (visit.status !== VisitStatus.FULLY_PAID && visit.status !== VisitStatus.REFUNDED && visit.status !== VisitStatus.CANCELLED && visit.hasCampaign) {
      items.push({
        key: 'updateTime',
        name: 'Update Time',
        iconProps: { iconName: 'CalendarReply' },
        onClick: () => UpdateTimeDialog.show({ visit, onClosed: this.onDialogClosed }),
      });
    }

    if (visit.availableActions.addInsurance) {
      items.push({
        key: 'addInsurance',
        name: (!visit.insuranceName ? 'Add' : 'Update') + ' Insurance',
        iconProps: { iconName: 'BankSolid' },
        disabled: !this.props.payers,
        onClick: () =>
          AddInsuranceDialog.show({
            visit,
            payers: this.props.payers!,
            onClosed: this.onDialogClosed,
            actions: { toast: this.props.actions.toast },
          }),
      });
    }

    if (visit.availableActions.cancelVisit) {
      items.push({
        key: 'cancelVisit',
        name: 'Cancel Visit',
        iconProps: { iconName: 'Cancel' },
        onClick: () => this.setState({ showCancelVisitDialog: true }),
      });
    }

    if (!visit.hasCampaign) {
      items.push({
        key: 'cloneVisit',
        name: 'Clone Visit',
        iconProps: { iconName: 'ConnectContacts' },
        onClick: () =>
          this.props.navigate!(
            ROUTES.createVisit + '?uid=' + visit.userAccountId + '&pid=' + visit.patientId + '&address=' + visit.addressLong + '&service=' + visit.visitServiceCode
          ),
      });

      if (this.context.enableChangeServiceType) {
        items.push({
          key: 'changeServiceOffering',
          name: 'Change Service Type',
          iconProps: { iconName: 'Flow' },
          onClick: () => ChangeServiceOfferingDialog.show({ visit, onSuccess: () => this.refreshVisit() }),
        });
      }
    }

    if (visit.status === VisitStatus.FULLY_PAID || (visit.status === VisitStatus.REFUNDED && visit.insuranceMemberId)) {
      items.push({
        key: 'generateClaim',
        name: 'Generate Claim',
        iconProps: { iconName: 'Generate' },
        onClick: () => {
          this.evaluateCurrentVisitForClaim();
        },
      });
    }

    if (this.context.enableReferrals) {
      items.push({
        key: 'referral',
        name: 'Referrals',
        iconProps: { iconName: 'ContactCard' },
        onClick: () => {
          this.setState({ showReferralPanel: true });
        },
      });
    }

    if (items.length > 0) {
      return (
        <DefaultButton
          text="Actions"
          data-tid="btn_actions"
          menuProps={{
            shouldFocusOnMount: true,
            items: items,
          }}
        />
      );
    } else {
      return null;
    }
  }

  private renderMap(): JSX.Element {
    const { visit } = this.props;
    const point = { lat: visit.latitudeAddress, lng: visit.longitudeAddress };
    return <HealHqMap height={260} center={point} points={[point]} />;
  }

  private renderClaimSection(): JSX.Element {
    const { claim } = this.props;

    if (!claim) {
      return <MessageBar>No claim for this visit.</MessageBar>;
    }

    return (
      <div>
        {this.renderFieldValue('State', claim.state)}
        {this.renderFieldValue('Submitted Date', claim.submittedDate ? moment(claim.submittedDate).format('L') : 'Not Submitted')}
        {this.renderFieldValue('Latest Update', claim.lastClaimMessageUpdate ? moment(claim.lastClaimMessageUpdate).format('L') : 'Not Submitted')}
        {this.renderFieldValue('Payer Paid', claim.payerPaid ? '$' + String(claim.payerPaid) : 'N/A')}
        {this.renderFieldValue('Patient Responsibility', claim.patientResponsibility ? '$' + String(claim.patientResponsibility) : 'N/A')}
        {this.renderFieldValue('Eligible', <InsuranceTransactionLinks referenceId={claim.costEstimationId} medicare={claim.payer.id === '00431'} />)}

        {claim.id && <Link href={`${ROUTES.claims}/${claim.id}`}>Open Claim Details...</Link>}
      </div>
    );
  }

  private onSubmit = (apiEndpoint: string, newValue: any): any => {
    if (apiEndpoint === 'updatePatient') {
      const patientData: EditPatientRequest = {
        ...this.state.patientData,
        ...newValue,
      };
      this.setState({ patientData });
      return HealAPI.editPatient(this.props.visit.patientId, patientData)
        .then((response) => {
          if (response.ok && response.data && (response.data.status === StatusOptions.OK || !response.data.status)) {
            this.props.actions.toast({
              type: 'success',
              title: 'Success',
              content: 'The patient information has been successfully updated',
            });
            this.refreshVisit();
            return true;
          } else {
            this.props.actions.toast({
              type: 'error',
              title: 'Error',
              content: (response.data || ({} as any)).description || 'The patient information could not be updated',
            });
            return false;
          }
        })
        .catch(() => false);
    }

    return HealAPI.updateField(this.props.visit.visitCode, apiEndpoint, newValue)
      .then((response) => {
        if (response.ok && response.data && (response.data.status === StatusOptions.OK || !response.data.status)) {
          this.refreshVisit();
          return true;
        } else {
          return false;
        }
      })
      .catch(() => false);
  };

  private onDialogClosed = (success: boolean) => {
    if (success) {
      this.refreshVisit();
    }
  };

  private refreshVisit(visitCode = this.props.visit.visitCode) {
    if (this.props.visit.hasCampaign) {
      this.props.actions.fetchOnSiteVisit(visitCode, true);
    } else {
      this.props.actions.fetchVisit(visitCode, true);
    }
  }

  private async onVisitUpdated(visit: Visit) {
    /*
     * Refresh visits by markets table view by dispatching VisitsAction.REFRESH_VISITS.
     * This action type is watched by visitsFlow() (saga.ts) and will refetch visits based on Visits UI selections.
     *
     * Because Saga watches actions and can fire off Redux state updates after requests, sometimes you have to
     * manually time your Redux state updates to come after Saga updates. What does this mean?
     *
     * We need a hack to let the Saga network calls finish and set the visit once we're confident it is done...
     * Here I'm intentionally showing the hack and reducing network calls.
     *
     * Other callbacks here are using fetchVisit(visit: visit, refresh:true) which results in
     * duplicate, redundant fetch visit requests and a 3 seconds delay to wait out asynchonous Saga updates.
     */

    this.setState({ codeOfVisitBeingUpdated: visit.visitCode });
    const shouldRefreshVisitsByMarketTable = this.props.location!.pathname === ROUTES.visits;
    if (shouldRefreshVisitsByMarketTable) {
      this.props.actions.refreshVisits();
    }

    setTimeout(
      () => {
        const userSwitchedAwayFromVisitToBeUpdated = this.state.codeOfVisitBeingUpdated !== this.props.visit.visitCode;
        this.setState({ codeOfVisitBeingUpdated: undefined });
        if (userSwitchedAwayFromVisitToBeUpdated) {
          return;
        }

        if (visit.hasCampaign) {
          this.props.actions.setOnSiteVisit(visit);
        } else {
          this.props.actions.setVisit(visit);
        }
      },
      shouldRefreshVisitsByMarketTable ? 2000 : 0
    );

    this.setState({ showCancelVisitDialog: false });
  }

  private getFormattedTime(time: number): string {
    if (this.props.visit.timezone) {
      return moment(time).tz(this.props.visit.timezone).format('ddd, MMMM DD YYYY, h:mm A z');
    }
    return '';
  }

  private getFormattedRequestedTimeSlot(): string {
    const { visit } = this.props;
    let requestedTime = '';
    const startTime = moment(visit.timeslotStartTime).tz(visit.timezone);
    const endTime = moment(visit.timeslotEndTime).tz(visit.timezone);
    if (!startTime || !endTime) {
      return '';
    }

    if (startTime.format('A') === endTime.format('A')) {
      requestedTime = `${startTime.format('h:mm')} - ${endTime.format('h:mm A z')}`;
    } else {
      requestedTime = `${startTime.format('h:mm A')} - ${endTime.format('h:mm A z')}`;
    }

    return visit.longerDateString + ', ' + requestedTime;
  }

  private getPaymentMethod(): string | JSX.Element {
    switch (this.props.visit.paymentType) {
      case 'APPLEPAY':
        return <img className="applepay" src={IMAGES.applePay} alt="Apple Pay" />;
      case 'GOOGLEPAY':
        return <img className="googlepay" src={IMAGES.googlePay} alt="Google Pay" />;
      case 'CARD':
        if (this.props.visit.cardLabel) {
          const card = this.props.visit.cardLabel.toLowerCase();
          return <div>{card[0].toUpperCase() + card.substr(1) + ' ' + this.props.visit.cardNumber}</div>;
        }
    }
    return 'N/A';
  }

  private getErrorMessage(content: string): string {
    return 'An error occurred while fetching ' + content + ', please try to refresh the page.';
  }

  private evaluateCurrentVisitForClaim() {
    this.props.actions.generateClaim(this.props.visit.visitCode, false, false);
  }

  private fetchPatientData(visit: Visit) {
    const patientData: EditPatientRequest = {
      firstName: visit.patientFirstName,
      lastName: visit.patientLastName,
      phoneNumber: visit.patientPhone,
      email: visit.patientEmail,
      relationship: visit.relationshipName,
      hasMedicareMedicaid: !!visit.patient?.hasMedicareMedicaid,
      hasMedicaid: visit.patient.hasMedicaid,
      dateOfBirth: new Date(visit.patientDob),
      gender: visit.genderName.toUpperCase(),
      avatarUrl: visit.patientAvatarUrl,
    };
    this.setState({ patientData });
  }

  private fetchPreferredDoctor(patientId: string) {
    this.setState({ preferredDoctor: { isFetching: true } });
    HealAPI.fetchPatientPreferredDoctor(patientId)
      .then((response: any) => {
        if (this.props.visit.patientId === patientId) {
          if (response.ok) {
            this.setState({ preferredDoctor: { data: response.data, isFetching: false } });
          } else {
            this.setState({
              preferredDoctor: {
                isFetching: false,
                error: this.getErrorMessage("this Patient's Preferred Provider"),
              },
            });
          }
        }
      })
      .catch(() => {
        if (this.props.visit.patientId === patientId) {
          this.setState({ preferredDoctor: { isFetching: false } });
        }
      });
  }

  private async downloadCarePlan() {
    this.setState({ carePlanDownloading: true });

    const filename = `${this.props.visit.visitCode}_CarePlan_${this.props.visit.longDateString}.pdf`;
    File.downloadPDF(this.props.carePlan!.download_url, filename).then((success) => {
      if (!success) {
        this.props.actions.toast({
          type: 'error',
          title: 'Error',
          content: this.getErrorMessage(`this Visit's Clinical Care Plan`),
        });
      }
      this.setState({ carePlanDownloading: false });
    });
  }
}

/**
 * Necessary per APP-9140.
 * @see https://jira.heal.com/browse/APP-9140
 * @see https://reactjs.org/docs/context.html#classcontexttype
 */
Component.contextType = FeatureFlagContext;

export function mapStateToProps(state: RootState, ownProps: OwnProps): StateProps {
  const subService = state.serviceOffering.services.data
    ?.find((service) => service.code === ownProps.visit.visitServiceCode)
    ?.subServices?.find((subService) => subService.code === ownProps.visit.subServiceCode);

  return {
    payers: state.visits.visitConfig.data && state.visits.visitConfig.data.payers,
    markets: state.markets.markets,
    claim: state.visits.visitSideCardClaim,
    carePlan: state.patientFiles.data[ownProps.visit.patientId]?.data?.filter((f) => f.context === 'careplans' && f.visit_code === ownProps.visit.visitCode)[0],
    subService,
  };
}

export function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    actions: bindActionCreators(
      {
        setVisitCode,
        setVisit,
        setOnSiteVisit,
        startVisit,
        endVisit,
        fetchVisit,
        refreshVisits: () => dispatch({ type: VisitsAction.REFRESH_VISITS }),
        fetchOnSiteVisit,
        toast,
        generateClaim,
        fetchOneLatestClaimByVisitCode,
      },
      dispatch
    ),
  };
}

export const VisitSidePanel = withRouter(connect<StateProps, DispatchProps, OwnProps, RootState>(mapStateToProps, mapDispatchToProps)(Component));
